"use client";

import { cn } from "@/lib/utils";
import {
  createContext,
  type HTMLAttributes,
} from "react";
import { Highlight, themes, type Language } from "prism-react-renderer";

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: string;
  showLineNumbers?: boolean;
};

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: "",
});

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) => {
  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          "group relative w-full overflow-hidden rounded-md border bg-background text-foreground",
          className
        )}
        {...props}
      >
        <div className="relative">
          {/* Light theme */}
          <Highlight
            theme={themes.oneLight}
            code={code.trim()}
            language={language as Language}
          >
            {({ className: preClassName, style, tokens, getLineProps, getTokenProps }) => (
              <pre
                className={cn(preClassName, "m-0 p-4 text-sm overflow-auto dark:hidden")}
                style={{ ...style, backgroundColor: 'transparent' }}
              >
                <code className="font-mono text-sm">
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {showLineNumbers && (
                        <span className="inline-block min-w-10 mr-4 text-right select-none text-muted-foreground">
                          {i + 1}
                        </span>
                      )}
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </code>
              </pre>
            )}
          </Highlight>

          {/* Dark theme */}
          <Highlight
            theme={themes.oneDark}
            code={code.trim()}
            language={language as Language}
          >
            {({ className: preClassName, style, tokens, getLineProps, getTokenProps }) => (
              <pre
                className={cn(preClassName, "m-0 p-4 text-sm overflow-auto hidden dark:block")}
                style={{ ...style, backgroundColor: 'transparent' }}
              >
                <code className="font-mono text-sm">
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {showLineNumbers && (
                        <span className="inline-block min-w-10 mr-4 text-right select-none text-muted-foreground">
                          {i + 1}
                        </span>
                      )}
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </code>
              </pre>
            )}
          </Highlight>

          {children && (
            <div className="absolute top-2 right-2 flex items-center gap-2">
              {children}
            </div>
          )}
        </div>
      </div>
    </CodeBlockContext.Provider>
  );
};
